<template>
	<AppLayout :title="trans('admin.users.title')">
		<form class="flex flex-col lg:flex-row items-stretch lg:items-end gap-3" @submit.prevent="filter">
			<LabeledObject full>
				<template #label>{{ trans('general.search') }}</template>
				<input v-model="searchTerm" type="text">
			</LabeledObject>
			<LabeledObject>
				<template #label>{{ trans('general.filter.by') }} {{ trans('general.filter.college.name') }}</template>
				<select v-model="searchCollegeId" class="lg:max-w-80">
					<option :value="null">{{ trans('general.filter.college.all') }}</option>
					<option v-for="college in colleges" :value="college.id">{{ college.name }}</option>
				</select>
			</LabeledObject>
			<PrimaryButton :color="Color.Blue" :type="ButtonType.Submit">
				<i class="fa-solid fa-search"/>
			</PrimaryButton>
		</form>
		<Pagination :resource="users"/>
		<Empty v-if="users.data.length === 0"/>
		<CardGrid v-else>
			<Card v-for="user in users.data" :key="`user-${user.id}`" :title="user.last_name" :subtitle="user.names">
				<template #badges>
					<Badge :color="Color.TrueAArEII" active>{{ user.level }}</Badge>
					<Badge class="cursor-pointer" @click.stop="copyTextToClipboard(`${user.id}`)">#{{ user.id }}</Badge>
				</template>
				<LabeledObject>
					<template #label>{{ trans('account.id_number') }}</template>
					<a @click.stop="copyTextToClipboard(`${user.id_number}`)">{{ user.id_number }}</a>
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('account.created_at') }}</template>
					<p v-if="user.created_at">{{ dayjs(user.created_at).format(trans('general.dayjs.date')) }}</p>
					<p v-else>{{ trans('general.n_a') }}</p>
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('account.college_programs.title') }}</template>
					<ol v-if="user.college_programs.length > 0" class="list-decimal list-outside ml-5">
						<li v-for="collegeProgram in user.college_programs">
							<span class="flex flex-col">
								<span>{{ collegeProgram.college.acronym }}</span>
								<span class="text-xs font-light">{{ collegeProgram.major.name }}, {{ collegeProgram.certification }}</span>
							</span>
						</li>
					</ol>
					<p v-else>{{ trans('general.n_a') }}</p>
				</LabeledObject>
				<template #footer>
					<Link v-if="canEdit" :as="SecondaryButton" :color="Color.Blue" :href="route('association.associates.edit', {user: user.id, returnUrl: route('association.associates.index', route().params)})">
						<i class="fa-solid fa-edit mr-2"/>
						{{ trans('general.edit') }}
					</Link>
					<a v-if="canManage" :href="route('association.associates.switch', user.id)">
						<SoftButton :color="Color.Purple">
							<i class="fa-solid fa-arrows-rotate mr-2"/>
							{{ trans('admin.users.switch') }}
						</SoftButton>
					</a>
					<SoftButton v-if="canDelete" :color="Color.Red" @click.stop="destroy(user.id)">
						<i class="fa-solid fa-trash mr-2"/>
						{{ trans('general.delete') }}
					</SoftButton>
				</template>
			</Card>
		</CardGrid>
		<Pagination :resource="users"/>
	</AppLayout>
</template>
<script setup lang="ts">
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {type User} from '@/Types/User'
import {Color} from '@/Types/Enums/Color'
import Empty from '@/Components/UI/Empty.vue'
import {Link, router} from '@inertiajs/vue3'
import {copyTextToClipboard} from '@/Helpers/general'
import Card from '@/Components/UI/Cards/Card.vue'
import {defineProps, type Ref, ref} from 'vue'
import AppLayout from '@/Layouts/AppLayout.vue'
import Badge from '@/Components/UI/Badges/Badge.vue'
import {ButtonType} from '@/Types/Enums/ButtonType'
import Pagination from '@/Components/UI/Pagination.vue'
import {type College} from '@/Types/Settings/College'
import CardGrid from '@/Components/UI/Cards/CardGrid.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import SoftButton from '@/Components/UI/Buttons/SoftButton.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type PaginatedModel} from '@/Types/Pagination/PaginatedModel'
import SecondaryButton from '@/Components/UI/Buttons/SecondaryButton.vue'

const props = defineProps<{
	page:number,
	search:string,
	canEdit:boolean,
	collegeId:number,
	canManage:boolean,
	canDelete:boolean,
	colleges:Array<College>,
	users:PaginatedModel<User>,
}>()

const searchTerm:Ref<string> = ref(props.search)
const searchCollegeId:Ref<number> = ref(props.collegeId)
const users:Ref<PaginatedModel<User>> = ref(props.users)

function filter():void{
	router.get(route('association.associates.index', {
		page: props.page,
		search: searchTerm.value,
		college_id: searchCollegeId.value,
	}))
}

function destroy(userId:number):void{
	alert(`Not implemented. User ID: ${userId}.`)
}
</script>